import MojitoServices from 'mojito/services';

import {iconColor} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {generateEmptyContentMessage} from '#core/application/modules/tiles/empty-content-message/index.js';
import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generateTertiaryButton} from '#core/application/modules/tiles/button/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';

const LOCATION = MojitoServices.Promotions.types.LOCATION;

export function virtualsFactory(PALETTE) {
    const {FONT} = PALETTE;

    const COUNTDOWN_LABELS_STYLE = {
        textAlign: 'center',
        lineHeight: '17px',
        textTransform: 'uppercase',
        fontSize: FONT.SIZE_XXSMALL,
        ...PALETTE.VIRTUALS_NEXT_EVENT_COUNTDOWN_TEXT,
    };

    const EVENT_NAME_TEXT_STYLE = {
        textTransform: 'uppercase',
        textAlign: 'left',
        fontSize: FONT.SIZE_LARGE,
        fontWeight: FONT.WEIGHT_BOLD,
        ...PALETTE.VIRTUALS_EVENT_NAME_TEXT,
    };
    const result = {
        VirtualSportPageView: {
            leftPromotions: {
                locationId: LOCATION.LEFT, // same as on home page
            },
            pageHeader: {
                icon: {
                    style: {
                        minWidth: '16px',
                        margin: '0 8px',
                    },
                },
            },
        },
        VirtualSportsMenu: {
            // This component is used together for desktop and mobile
            // But in desktop only sportsContainer and menuItem are used
            hideHeader: true, // Never show the header
            ...(isMobile()
                ? {
                      // Mobile
                      container: {
                          style: resolveBackground(PALETTE.MOBILE_SPORT_SELECTOR_CONTAINER),
                      },
                      sportsMenuRow: generatePivotsRow(PALETTE.MOBILE_SPORT_SELECTOR),
                      menuRowSkeleton: {
                          container: {
                              style: resolveBackground(PALETTE.MOBILE_SPORT_SELECTOR_CONTAINER),
                          },
                          linkPlaceholder: {
                              style: generateSkeletonPart(PALETTE.SKELETON),
                          },
                      },
                  }
                : {
                      // Desktop
                      container: {
                          style: {
                              ...resolveBackground(PALETTE.DESKTOP_VIRTUALS_SPORT_MENU_BG),
                              ...(PALETTE.DESKTOP_VIRTUALS_SHOW_SHADOW
                                  ? {boxShadow: PALETTE.DESKTOP_VIRTUALS_SHADOW}
                                  : {}),
                          },
                      },

                      menuRowSkeleton: {
                          container: {
                              style: resolveBackground(PALETTE.DESKTOP_VIRTUALS_SPORT_MENU_BG),
                          },
                          linkPlaceholder: {
                              style: generateSkeletonPart(PALETTE.SKELETON),
                          },
                      },
                  }),
        },
        VirtualSportsMenuItem: {
            timer: {
                timerWrapper: {
                    style: {
                        justifyContent: 'center',
                    },
                },
                timeString: {
                    style: {
                        flexGrow: 0,
                        width: 'fit-content',
                    },
                },
            },
            infoContainer: {
                style: {
                    alignItems: 'center',
                    _mobile_alignItems: 'flex-start',
                },
            },
            eventStatusContainer: {
                style: {
                    overflow: 'hidden',
                    padding: 0,

                    borderRadius: '7px',
                    ...resolveBackground(PALETTE.VIRTUALS_NEXT_EVENT_COUNTDOWN_BG),
                    ...COUNTDOWN_LABELS_STYLE, // right how Mojito do this in their refbook even despite it's a FlexBox 🤷‍♂️
                },
            },

            ...(isMobile()
                ? generateVirtualSportsMenuItemForMobile(PALETTE.MOBILE_SPORT_SELECTOR)
                : generateVirtualSportsMenuItemForDesktop(PALETTE.DESKTOP_SPORT_MENU)),
            selected: isMobile()
                ? generateVirtualSportsMenuItemForMobile(PALETTE.MOBILE_SPORT_SELECTOR, true)
                : generateVirtualSportsMenuItemForDesktop(PALETTE.DESKTOP_SPORT_MENU, true),
        },
        VirtualEventsCoupon: {
            emptyContent: generateEmptyContentMessage(PALETTE.VIRTUALS_NO_CONTENT),
            eventLabelContainer: {
                style: {
                    _mobile_padding: '0 8px',
                },
            },
            raceCard: {
                eachWayContainer: {
                    style: {
                        _desktop_textAlign: 'right',
                    },
                },
                raceCardInfo: {
                    container: {
                        style: {
                            ...(isMobile() ? resolveBackground(PALETTE.VIRTUALS_EVENT_SELECTOR_BG) : {}),
                        },
                    },
                    header: {
                        style: {
                            backgroundColor: 'transparent', // override our factory
                            borderWidth: 0,
                        },
                    },
                    headerTitleButton: {
                        style: {
                            base: {
                                ...EVENT_NAME_TEXT_STYLE,
                                backgroundColor: 'transparent', // override our factory
                            },
                        },
                    },
                    body: {
                        style: {
                            backgroundColor: 'transparent',
                        },
                    },
                    ...(isMobile()
                        ? {
                              // In mobile DOM structure slightly changed, and info details are placed on same bg as VIRTUALS_EVENT_SELECTOR_BG
                              // So need fix text colors
                              firstRow: {
                                  style: {
                                      fontSize: FONT.SIZE_XSMALL,
                                      ...PALETTE.VIRTUALS_MOBILE_RACE_DETAILS_TEXT,
                                  },
                              },
                              secondRow: {
                                  style: {
                                      fontSize: FONT.SIZE_XXSMALL,
                                      ...PALETTE.VIRTUALS_MOBILE_RACE_DETAILS_TEXT,
                                  },
                              },
                          }
                        : {}),
                },
                runnerInfo: {
                    numbersAndSilkContainer: {
                        style: {
                            _mobile_padding: '0 8px 0',
                        },
                    },
                },
            },
            eventLabel: {
                style: EVENT_NAME_TEXT_STYLE,
            },
            eventSelectorContainer: {
                style: {
                    ...resolveBackground(PALETTE.VIRTUALS_EVENT_SELECTOR_BG),
                    borderStyle: 'solid',
                    borderColor: PALETTE.VIRTUALS_EVENT_SELECTOR_BOTTOM_DIVIDER,
                    borderWidth: '1px',
                },
            },
            eventSelectorMenuRow: merge(generatePivotsRow(PALETTE.VIRTUALS_EVENT_SELECTOR), {
                style: {
                    container: {
                        _mobile_height: 'initial',
                    },
                },
            }),
            streamingContainer: {
                style: {
                    ...resolveBackground(PALETTE.VIRTUALS_STREAM_CONTAINER_BG),
                },
            },
        },
        VirtualStream: {
            maxCompactModeWidth: 800,
            style: {
                modes: {
                    wide: {
                        minHeight: '354px',
                    },
                    compact: {
                        minHeight: '295px',
                    },
                },
                buttonContainer: {
                    padding: '0px 32px',
                },
            },
            button: generateTertiaryButton(PALETTE.STREAM_BUTTON),
            buttonIcon: iconColor(PALETTE.STREAM_BUTTON.TERTIARY_BUTTON_ICON_COLOR),
        },
        VirtualEventsCouponSkeleton: {
            streamingSkeleton: {
                style: generateSkeletonPart(PALETTE.SKELETON),
            },
            eventNameLabelSkeleton: {
                style: generateSkeletonPart(PALETTE.SKELETON),
            },
            eventSelectorItemSkeleton: {
                style: generateSkeletonPart(PALETTE.SKELETON),
            },
        },
    };

    return result;
}

function generateVirtualSportsMenuItemForDesktop(LIST_ITEM, isSelected) {
    const ITEM_BORDER_STYLE = LIST_ITEM.LIST_ITEM_SHOW_SEPARATOR
        ? {
              borderWidth: '0 0 1px 0',
              borderColor: LIST_ITEM.LIST_ITEM_SEPARATOR_COLOR,
              borderStyle: 'solid',
          }
        : {};

    return {
        container: {
            style: {
                ...ITEM_BORDER_STYLE,
                ...resolveBackground(isSelected && LIST_ITEM.LIST_ITEM_SELECTED_BG),
            },
        },
        sportIcon: {
            style: {
                fill: isSelected ? LIST_ITEM.LIST_ITEM_SELECTED_ICON_COLOR : LIST_ITEM.LIST_ITEM_ICON_COLOR,
            },
        },

        sportLabel: {
            style: {
                ...LIST_ITEM.LIST_ITEM_TEXT,
                ...(isSelected ? LIST_ITEM.LIST_ITEM_SELECTED_TEXT : LIST_ITEM.LIST_ITEM_TEXT),
            },
        },
    };
}

function generateVirtualSportsMenuItemForMobile(PIVOT_TILE, isSelected) {
    return {
        sportIcon: {
            style: {
                fill: isSelected ? PIVOT_TILE.PIVOT_SELECTED_ICON_COLOR : PIVOT_TILE.PIVOT_ICON_COLOR,
            },
        },

        sportLabel: {
            style: isSelected ? PIVOT_TILE.PIVOT_SELECTED_TEXT : PIVOT_TILE.PIVOT_TEXT,
        },
    };
}
